import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import { AlertTriangle, Filter, Loader2, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SEVERITIES = ['high', 'medium', 'low'];

const ExceptionsDashboard = () => {
  const [activeCompany] = useActiveCompany();
  const [filter, setFilter] = useState('all');
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);

  const fetchExceptions = useCallback(async () => {
    setLoading(true);
    try {
      const qs = activeCompany ? `?company_id=${activeCompany}` : '';
      const { data } = await axios.get(`${API_URL}/attendance/exceptions${qs}`, { withCredentials: true });
      setExceptions(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load exceptions');
      setExceptions([]);
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => {
    fetchExceptions();
  }, [fetchExceptions]);

  const totals = {
    all: exceptions.length,
    high: exceptions.filter((e) => (e.severity || '').toLowerCase() === 'high').length,
    medium: exceptions.filter((e) => (e.severity || '').toLowerCase() === 'medium').length,
    low: exceptions.filter((e) => (e.severity || '').toLowerCase() === 'low').length,
    resolved: exceptions.filter((e) => e.is_resolved || e.status === 'resolved').length,
  };

  const filteredExceptions = exceptions.filter(
    (e) => filter === 'all' || (e.severity || '').toLowerCase() === filter,
  );

  const resolve = async (id) => {
    setResolving(id);
    try {
      await axios.post(`${API_URL}/attendance/exceptions/${id}/resolve`, {}, { withCredentials: true });
      toast.success('Exception marked resolved');
      fetchExceptions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resolve');
    } finally {
      setResolving(null);
    }
  };

  const exportCsv = () => {
    if (!exceptions.length) {
      toast.error('Nothing to export');
      return;
    }
    downloadCSV(
      exceptions.map((e) => ({
        Employee_Code: e.employee_code || '',
        Employee_Name: e.employee_name || '',
        Date: e.attendance_date || '',
        Type: e.exception_type || '',
        Severity: e.severity || '',
        Description: e.description || '',
        Resolved: e.is_resolved ? 'yes' : 'no',
      })),
      `attendance_exceptions_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    toast.success('Exceptions exported');
  };

  const severityClass = (s) => {
    const k = (s || '').toLowerCase();
    if (k === 'high') return 'bg-red-50 text-red-700 border-red-300';
    if (k === 'medium') return 'bg-amber-50 text-amber-700 border-amber-300';
    if (k === 'low') return 'bg-blue-50 text-blue-700 border-blue-300';
    return 'bg-slate-50 text-slate-700 border-slate-200';
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="exceptions-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <AlertTriangle className="inline-block w-8 h-8 mr-2 mb-1 text-amber-600" />
              Attendance Exceptions
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Review and resolve attendance anomalies and exceptions
            </p>
          </div>
          <Button variant="outline" onClick={exportCsv} data-testid="exceptions-export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Total Exceptions</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1 font-mono">{totals.all}</p>
          </div>
          <div className="bg-white border border-red-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-red-500">High Severity</p>
            <p className="text-2xl font-semibold text-red-600 mt-1 font-mono">{totals.high}</p>
          </div>
          <div className="bg-white border border-amber-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-amber-500">Medium Severity</p>
            <p className="text-2xl font-semibold text-amber-600 mt-1 font-mono">{totals.medium}</p>
          </div>
          <div className="bg-white border border-green-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-green-500">Resolved</p>
            <p className="text-2xl font-semibold text-green-600 mt-1 font-mono">{totals.resolved}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setFilter('all')}
            data-testid="exception-filter-all"
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-[#0052FF] text-white'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            All ({totals.all})
          </button>
          {SEVERITIES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`exception-filter-${s}`}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === s
                  ? s === 'high'
                    ? 'bg-red-600 text-white'
                    : s === 'medium'
                      ? 'bg-amber-600 text-white'
                      : 'bg-blue-600 text-white'
                  : `bg-white border ${
                      s === 'high'
                        ? 'border-red-200 text-red-700 hover:bg-red-50'
                        : s === 'medium'
                          ? 'border-amber-200 text-amber-700 hover:bg-amber-50'
                          : 'border-blue-200 text-blue-700 hover:bg-blue-50'
                    }`
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)} ({totals[s]})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : filteredExceptions.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500" data-testid="exceptions-empty">
            <AlertTriangle className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm">No {filter !== 'all' ? `${filter}-severity ` : ''}exceptions found.</p>
            <p className="text-xs text-slate-400 mt-1">
              Exceptions are auto-detected when attendance is calculated — missing punches, invalid sequences, duplicates, roster mismatches and absent-without-leave cases all show up here.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredExceptions.map((e) => (
              <div
                key={e.id || e._id}
                data-testid={`exception-${e.id || e._id}`}
                className={`bg-white border rounded-lg p-6 ${severityClass(e.severity)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="mb-3">
                      <p className="font-semibold text-slate-900">{e.exception_type || 'Exception'}</p>
                      <p className="text-xs text-slate-500">
                        {e.attendance_date || ''} • Severity: {(e.severity || 'unknown').toUpperCase()}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-slate-500">Employee</p>
                        <p className="text-sm font-medium text-slate-900">{e.employee_name || '—'}</p>
                        <p className="text-xs text-slate-500 font-mono">{e.employee_code || e.employee_id}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Status</p>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            e.is_resolved
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                          }`}
                        >
                          {e.is_resolved ? 'Resolved' : 'Pending'}
                        </span>
                      </div>
                    </div>
                    {e.description && (
                      <div className="bg-white/50 rounded p-3">
                        <p className="text-sm text-slate-700">{e.description}</p>
                      </div>
                    )}
                  </div>
                  {!e.is_resolved && (
                    <Button
                      size="sm"
                      data-testid={`resolve-${e.id || e._id}`}
                      disabled={resolving === (e.id || e._id)}
                      onClick={() => resolve(e.id || e._id)}
                      className="ml-4 bg-[#0052FF] hover:bg-[#0043D1] text-white"
                    >
                      {resolving === (e.id || e._id) ? '…' : 'Mark Resolved'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-xs text-slate-500 flex items-center gap-1">
          <Filter className="w-3 h-3" /> Auto-detected. High-severity exceptions block payroll calculation until resolved.
        </div>
      </div>
    </MainLayout>
  );
};

export default ExceptionsDashboard;
